import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { User, Building2 } from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'
// Handshake, Briefcase - used when B2B/Partnership forms are enabled

interface RegistrationOption {
  icon: React.ComponentType<{ className?: string }>
  titleKey: string
  descriptionKey: string
  path: string
}

const REGISTRATION_OPTIONS: RegistrationOption[] = [
  {
    icon: User,
    titleKey: 'auth.individualProvider',
    descriptionKey: 'auth.individualProviderDesc',
    path: '/register',
  },
  {
    icon: Building2,
    titleKey: 'auth.organizationProvider',
    descriptionKey: 'auth.organizationProviderDesc',
    path: '/provider-registration',
  },
  // B2B and Partnership forms commented out for now
  // {
  //   icon: Briefcase,
  //   titleKey: 'auth.b2bProjectRequest',
  //   descriptionKey: 'auth.b2bProjectRequestDesc',
  //   path: '/b2b-project-request',
  // },
  // {
  //   icon: Handshake,
  //   titleKey: 'auth.partnershipRequest',
  //   descriptionKey: 'auth.partnershipRequestDesc',
  //   path: '/partnership-request',
  // },
]

/**
 * Registration options section component
 */
export function RegistrationOptions() {
  const navigate = useNavigate()
  const { t } = useLocalization()

  return (
    <div className="mt-6 sm:mt-10 pt-5 sm:pt-8 border-t border-gray-200/60 space-y-4 sm:space-y-5">
      {/* Divider */}
      <div className="relative" role="separator" aria-label="Registration options divider">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-4 py-1 text-xs font-semibold text-gray-500 tracking-wider">
            {t('auth.newToWtdcare')}
          </span>
        </div>
      </div>

      {/* Registration Buttons */}
      <div className="grid gap-3">
        {REGISTRATION_OPTIONS.map((option, index) => {
          const Icon = option.icon
          return (
            <Button
              key={option.path}
              variant="outline"
              className="group w-full h-auto py-4 px-4 justify-start text-start border-2 border-gray-200 hover:border-primary hover:bg-gradient-to-br hover:from-primary/5 hover:to-primary/10 transition-all duration-200 hover:shadow-md hover:scale-[1.02] active:scale-[0.98] rounded-xl"
              onClick={() => navigate(option.path)}
            >
              <div className="flex items-center gap-4 w-full">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-colors duration-200">
                  <Icon className="w-5 h-5 text-primary group-hover:scale-110 transition-transform duration-200" aria-hidden="true" />
                </div>
                <div className="flex flex-col items-start flex-1 min-w-0">
                  <span className="font-semibold text-primary text-sm group-hover:text-primary/90 transition-colors">
                    {t(option.titleKey)}
                  </span>
                  <span className="text-xs text-muted-foreground font-normal mt-0.5 line-clamp-1">
                    {t(option.descriptionKey)}
                  </span>
                </div>
              </div>
            </Button>
          )
        })}
      </div>
    </div>
  )
}
